import React, {useRef} from 'react';
import {TextInput as RNTextInput, View} from 'react-native';

import Icon from '/component/base/Icon';
import KeyboardInput from '/component/base/KeyboardInput';
import Text from '/component/base/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {ABOUT_MAX_LENGTH, ABOUT_WARN} from '../../ManageMySite.constants';
import {StaticFocusable} from '../../sections/StaticFocusable';
import {SectionTitle} from '../../widgets/SectionTitle';

import {styles} from './AboutSection.style';
import type {AboutSectionProps} from './AboutSection.types';

const {aboutSection} = ManageMySiteTestIDs;

const getOverLimitError = (charCount: number) =>
  tt(
    'errAboutOver',
    `Please remove ${charCount - ABOUT_MAX_LENGTH} characters.`,
  );

const getWarningMessage = (charCount: number) =>
  tt(
    'warnAboutLimit',
    `Almost at limit, ${ABOUT_MAX_LENGTH - charCount} characters remaining.`,
  );

const ErrorMessage = ({
  testID,
  charCount,
  error,
  isWarning,
  isError,
}: {
  charCount: number;
  error: string;
  isWarning: boolean;
  isError: boolean;
  testID: string;
}) => {
  const message = String(
    isError
      ? error || getOverLimitError(charCount)
      : getWarningMessage(charCount),
  );

  const textTestId = isError
    ? aboutSection.errorText
    : aboutSection.warningText;

  const iconTestId = isError
    ? aboutSection.errorIcon
    : aboutSection.warningIcon;

  return (
    <StaticFocusable
      testID={testID}
      role="alert"
      label={message}
      style={styles.errorRow}>
      <View testID={iconTestId} accessible={false}>
        <Icon
          name={isError ? 'info' : 'alert'}
          style={[isWarning && styles.warningIcon, isError && styles.errorIcon]}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
        />
      </View>

      <View testID={textTestId} accessible={false}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          accessible={false}
          style={[
            isWarning && styles.warningText,
            isError && styles.errorText,
          ]}>
          {message}
        </Text>
      </View>
    </StaticFocusable>
  );
};

export const AboutSection = ({
  value = '',
  onChange,
  hasSubmitted,
  error = '',
  onBlur,
}: AboutSectionProps) => {
  const inputRef = useRef<RNTextInput>(null);

  const charCount = value.length;
  const isWarning = charCount >= ABOUT_WARN && charCount <= ABOUT_MAX_LENGTH;
  const isOver = charCount > ABOUT_MAX_LENGTH;
  const showError = isOver || (hasSubmitted && !!error);
  const showWarning = isWarning && !showError;

  const messageTestId = showError
    ? aboutSection.error
    : showWarning
    ? aboutSection.warning
    : '';

  const currentErrorMessage = error || getOverLimitError(charCount);

  const aboutAccessibilityLabel = showError
    ? value
      ? `${value}, About me, Error: ${currentErrorMessage}`
      : `About me, Error: ${currentErrorMessage}`
    : value
    ? `${value}, About me`
    : 'About me';

  return (
    <View
      style={styles.section}
      testID={aboutSection.container}
      accessible={false}>
      <SectionTitle title={tt('about', 'About Me')} />

      <StaticFocusable
        label="Share your journey and what makes you unique. Max 1000 characters."
        role="text"
        testID={aboutSection.subLabel}
        style={styles.subLabel}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          testID={aboutSection.subLabelText}
          style={styles.subLabelText}
          accessible={false}>
          Share your journey and what makes you unique. Max 1000 characters.
        </Text>
      </StaticFocusable>

      <KeyboardInput
        testID={aboutSection.input}
        ref={inputRef}
        value={value}
        onChangeText={onChange}
        onBlur={onBlur}
        multiline
        scrollEnabled
        inputProps={{
          showsVerticalScrollIndicator: true,
          submitBehavior: 'submit',
          returnKeyType: 'next',
        }}
        accessible
        accessibilityLabel={aboutAccessibilityLabel}
        accessibilityHint="Tell your story and what makes you unique."
        accessibilityRole="none"
        placeholder="Tell your story ..."
        placeholderTextColor="#9CA3AF"
        style={[
          styles.textArea,
          showError && styles.textAreaError,
          isWarning && styles.textAreaWarning,
        ]}
        allowFontScaling
        maxFontSizeMultiplier={2}
      />

      <StaticFocusable
        role="text"
        label={`${charCount} of ${ABOUT_MAX_LENGTH} characters.`}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          testID={aboutSection.charCount}
          accessible={false}
          style={[
            styles.charCount,
            isWarning && styles.charCountWarning,
            isOver && styles.charCountError,
          ]}>
          {charCount} / {ABOUT_MAX_LENGTH} {tt('characters', 'characters')}
        </Text>
      </StaticFocusable>

      {(showError || showWarning) && (
        <ErrorMessage
          key={showError ? 'error' : 'warning'}
          charCount={charCount}
          error={error}
          isWarning={showWarning}
          isError={showError}
          testID={messageTestId}
        />
      )}
    </View>
  );
};

export default AboutSection;
